import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * DRB-62 — className overrides used to SILENTLY DO NOTHING.
 *
 * This was plain `clsx`, which only concatenates. So `<Container className="max-w-md">` emitted
 * BOTH `max-w-site` and `max-w-md` and the winner was decided by CSS source order, not by intent.
 * `max-w-site` (1280px) won, which is why the sign-in card rendered nearly full-width — measured
 * live at 1150px on a 1280px viewport. Scott, 2026-08-18: "is the login panel enormous?"
 *
 * That silence is the real defect: every `className` override anywhere in this app may or may not
 * have applied, with no error and no tell. Fixing it here fixes the class, not the one page.
 *
 * ⚠️ Plain `twMerge` is NOT sufficient and I verified that before trusting it: it only knows
 * Tailwind's DEFAULT scale, so it left `max-w-site max-w-md` untouched — the naive fix would have
 * shipped and changed nothing. Custom theme values must be registered explicitly, and every one
 * below is covered by a test in cn.test.ts so a new theme value cannot quietly reintroduce this.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // tailwind.config.js -> theme.extend.maxWidth
      "max-w": ["max-w-site"],
      // theme.extend.borderRadius
      rounded: ["rounded-soft", "rounded-card", "rounded-pill"],
      // theme.extend.fontFamily
      "font-family": ["font-condensed"],
    },
  },
});

/** Tailwind-aware className combiner: later classes WIN over earlier conflicting ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
